import React, { useEffect, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import {
  getAllMilestonesApi,
  deleteMilestoneApi,
  getMilestoneBinApi,
  restoreMilestoneApi,
} from "../../Constants/api";
import SweetAlert from "react-bootstrap-sweetalert";
import Table from "../../../../../../Components/Table";
import { getImageUrl } from "../../utils/imageLoader";

const AllMilestones = () => {
  const [data, setData] = useState([]);
  const [activePage, setActivePage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsCountPerPage, setItemsCountPerPage] = useState(10);
  const [alert, setAlert] = useState(null);
  const [isBin, setIsBin] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [imageUrls, setImageUrls] = useState({});
  const history = useHistory();

  const fetchData = async (page = 1, sizePerPage = 10) => {
    setIsLoading(true);
    try {
      const response = isBin
        ? await getMilestoneBinApi(page, sizePerPage)
        : await getAllMilestonesApi(page, sizePerPage);

      if (response?.data) {
        setData(response.data.data || []);
        setTotalItems(response.data.count || 0);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData(activePage, itemsCountPerPage);
  }, [activePage, itemsCountPerPage, isBin]);

  // Convert SVG images to data URLs
  useEffect(() => {
    const convertImages = async () => {
      const newImageUrls = {};

      for (const milestone of data) {
        if (milestone.imageURL) {
          const imageUrl = await getImageUrl(milestone.imageURL);
          if (imageUrl) {
            newImageUrls[milestone._id] = imageUrl;
          } else {
            // If conversion fails, still use original URL
            newImageUrls[milestone._id] = milestone.imageURL;
          }
        }
      }

      setImageUrls(newImageUrls);
    };

    if (data.length > 0) {
      convertImages();
    }
  }, [data]);

  const handleTableChange = (type, { page, sizePerPage }) => {
    setActivePage(page);
    setItemsCountPerPage(sizePerPage);
  };

  const handleDelete = async (id) => {
    try {
      await deleteMilestoneApi(id);
      setAlert(
        <SweetAlert
          success
          title="Deleted!"
          onConfirm={() => {
            setAlert(null);
            fetchData(activePage, itemsCountPerPage);
          }}
        >
          Milestone has been deleted.
        </SweetAlert>
      );
    } catch (error) {
      console.error("Error deleting:", error);
      setAlert(
        <SweetAlert
          danger
          title="Error!"
          onConfirm={() => setAlert(null)}
        >
          Something went wrong.
        </SweetAlert>
      );
    }
  };

  const handleRestore = async (id) => {
    try {
      await restoreMilestoneApi(id);
      setAlert(
        <SweetAlert
          success
          title="Restored!"
          onConfirm={() => {
            setAlert(null);
            fetchData(activePage, itemsCountPerPage);
          }}
        >
          Milestone has been restored.
        </SweetAlert>
      );
    } catch (error) {
      console.error("Error restoring:", error);
      setAlert(
        <SweetAlert
          danger
          title="Error!"
          onConfirm={() => setAlert(null)}
        >
          Something went wrong.
        </SweetAlert>
      );
    }
  };


  const confirmDelete = (id) => {
    setAlert(
      <SweetAlert
        warning
        showCancel
        confirmBtnText="Yes, delete it!"
        confirmBtnBsStyle="danger"
        title="Are you sure?"
        onConfirm={() => handleDelete(id)}
        onCancel={() => setAlert(null)}
        focusCancelBtn
      >
        You will not be able to recover this milestone!
      </SweetAlert>
    );
  };

  const confirmRestore = (id) => {
    setAlert(
      <SweetAlert
        warning
        showCancel
        confirmBtnText="Yes, restore it!"
        confirmBtnBsStyle="success"
        title="Are you sure?"
        onConfirm={() => handleRestore(id)}
        onCancel={() => setAlert(null)}
        focusCancelBtn
      >
        You want to restore this milestone!
      </SweetAlert>
    );
  };


  const columns = [
    {
      text: "S.No",
      dataField: "sno",
      formatter: (cell, row, rowIndex) => {
        return (activePage - 1) * itemsCountPerPage + rowIndex + 1;
      },
    },
    {
      text: "Thumbnail",
      dataField: "imageURL",
      formatter: (cell, row) => {
        // Use converted image URL if available, otherwise use original
        const displayUrl = imageUrls[row._id] || cell;

        return (
          <div style={{ width: "50px", height: "50px", borderRadius: "4px", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#f5f5f5" }}>
            {displayUrl ? (
              <img
                src={displayUrl}
                alt={row.title}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                crossOrigin="anonymous"
                loading="lazy"
                onError={(e) => {
                  // Fallback to placeholder
                  e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='50' height='50'%3E%3Crect fill='%23e0e0e0' width='50' height='50'/%3E%3Ctext x='50%25' y='50%25' text-anchor='middle' dy='.3em' fill='%23999' font-size='10'%3ENo Image%3C/text%3E%3C/svg%3E";
                }}
              />
            ) : (
              <span style={{ color: "#999", fontSize: "12px" }}>No Image</span>
            )}
          </div>
        );
      },
    },
    {
      text: "Title",
      dataField: "title",
      formatter: (cell) => cell?.length > 10 ? cell.substring(0, 10) + "..." : cell
    },
    {
      text: "Description",
      dataField: "description",
      formatter: (cell) => cell?.length > 50 ? cell.substring(0, 50) + "..." : cell
    },
    {
      text: "Points",
      dataField: "points",
    },
    {
      text: "Days",
      dataField: "days",
    },
    {
      text: "Duration",
      dataField: "minutes",
      formatter: (cell) => cell ? `${cell} mins` : "-"
    },
    {
      text: "Actions",
      dataField: "actions",
      formatter: (cell, row) => (
        <div>
          {!isBin ? (
            <>
              <button
                className="btn btn-primary btn-sm mr-2"
                onClick={() => history.push(`/dashboard/milestones/edit/${row._id}`)}
              >
                Edit
              </button>
              <button
                className="btn btn-danger btn-sm"
                onClick={() => confirmDelete(row._id)}
              >
                Delete
              </button>
            </>
          ) : (
            <button
              className="btn btn-success btn-sm"
              onClick={() => confirmRestore(row._id)}
            >
              Restore
            </button>
          )}

        </div>
      ),
    },
  ];

  return (
    <div className="card">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h3>{isBin ? "Milestone Bin" : "All Milestones"}</h3>
        <div>
          <button className={`btn ${isBin ? 'btn-secondary' : 'btn-info'} mr-2`} onClick={() => { setIsBin(!isBin); setActivePage(1); }}>
            {isBin ? "View Active" : "View Bin"}
          </button>
          {!isBin && (
            <Link to="/dashboard/milestones/create" className="btn btn-warning">
              Create New
            </Link>
          )}
        </div>
      </div>
      <div className="card-body">
        <Table
          rows={data}
          columns={columns}
          page={activePage}
          sizePerPage={itemsCountPerPage}
          totalSize={totalItems}
          onTableChange={handleTableChange}
          isLoading={isLoading}
        />
      </div>
      {alert}
    </div>
  );
};

export default AllMilestones;
